import { createSelector } from 'reselect';
import get from 'lodash/get';
import memoize from 'lodash/memoize';
import { selectors as i18nSelectors } from 'mage-i18n';
import IntlMessageFormat from 'intl-messageformat';

import { appState } from './utils';
import { getCurrentRegion } from './data';
import { getWidgetCookieSafe } from './widget';
import { ContractOnboardingType } from '@mjolnir/mf-fields-ts/src/enm/ContractOnboardingType';
import { BpcPayload, ExitPoint } from '@/bpc/types/BpcPayload';
import { ContractOnboardingStatus } from '@mjolnir/mf-fields-ts/src/enm/ContractOnboardingStatus';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import { Country } from '@mjolnir/mf-fields-ts/src/enm/Country';
import { getContractOnboardingRegionFromKrn } from '@/bpc/utils/contractOnboarding';

export const getBpcState = createSelector(appState, (state): BpcPayload => state?.bpc);

export const getBpcPayload = createSelector(
  getBpcState,
  (bpcState: BpcPayload): Payload | undefined => bpcState?.data,
);

export const getBpcStatus = createSelector(
  getBpcState,
  (bpcState: BpcPayload): ContractOnboardingStatus => bpcState?.status,
);

export const getContractKrn = createSelector(
  getBpcState,
  (bpcState: BpcPayload): string | undefined => bpcState?.krn,
);

export const getExitPoints = createSelector(
  getBpcState,
  (bpcState: BpcPayload): ExitPoint[] | undefined => bpcState?.exit_points,
);

export const getContractCountryKey = createSelector(
  getBpcPayload,
  (bpcPayload: Payload | undefined): Country | undefined =>
    bpcPayload?.business_information?.registered_address?.country,
);

export const getBusinessName = createSelector(
  getBpcPayload,
  (bpcPayload: Payload | undefined): string | undefined =>
    ({
      [ContractOnboardingType.KLARNA_POINT_OF_SALES]:
        bpcPayload?.business_information?.company_name,
      [ContractOnboardingType.UPSELL_WEBSTORE_TO_KPOS]: bpcPayload?.in_stores?.name,
    }[bpcPayload?.contract_onboarding_type || '']),
);

export const getContractOnboardingType = createSelector(
  getBpcPayload,
  (bpcPayload: Payload | undefined): ContractOnboardingType | undefined =>
    bpcPayload?.contract_onboarding_type,
);

export const getContractRegion = createSelector(
  getCurrentRegion,
  getContractKrn,
  (currentRegion, contractKrn) => {
    const defaultRegion = currentRegion;
    const region = getContractOnboardingRegionFromKrn(defaultRegion, contractKrn);
    return region?.toLowerCase ? region.toLowerCase() : region;
  },
);

export const getKrnFromWidgetToken = createSelector(
  getWidgetCookieSafe,
  (cookie): string => cookie?.contractKrn,
);

// Return the key for a translation that depends on bpc information.
// Current format is:
// en:
//   some:
//     key:
//       country[country_code|contract_onboarding_type|default]:
//
// Where country is optional. Check keyPriority for the fallback chain
export const getBusinessAwareTranslationKey = createSelector(
  i18nSelectors.translations,
  getContractCountryKey,
  getContractOnboardingType,
  (translations, bpcCountry, contractOnboardingType) =>
    memoize(
      (
        tKey: string,
        country: Country | undefined = bpcCountry,
        contractType: ContractOnboardingType | undefined = contractOnboardingType,
      ): string => {
        // Get the english tree for the given label
        const mainTranslations = get(translations, `en.${tKey}`);

        // Try to find one of these keys.
        const keyPriority = [`${country}`, contractType, `default`, ''];

        let result;
        while (result === undefined && keyPriority.length) {
          const key = keyPriority.shift();
          if (get(mainTranslations, `${key}`)) {
            result = key;
          }
        }

        // Returns the first one found or the key itself
        return `${tKey}${result ? `.${result}` : ''}`;
      },
    ),
);

// Returns a translator that uses merchant information to get the correct key
export const getBusinessAwareTranslator = createSelector(
  i18nSelectors.translations,
  i18nSelectors.locale,
  getBusinessAwareTranslationKey,
  (
    translations: RecordOfRecords,
    locale: string,
    getKey: (key: string, country?: Country, contractType?: ContractOnboardingType) => string,
  ): TranslatorFunction => (tKey, params, fallbackString): string => {
    const finalKey = getKey(tKey);

    // Try to translate in the following priority order:
    //  - current locale
    //  - its first two letters
    //  - the given fallback string
    //  - the original english string
    const translation: string =
      get(translations, `${locale}.${finalKey}`) ||
      get(translations, `${locale.substring(0, 2)}.${finalKey}`) ||
      fallbackString ||
      get(translations, `en.${finalKey}`);

    if (params) {
      try {
        const intlMessageFormatter = new IntlMessageFormat(translation, locale);
        const textOrPartsOfText = intlMessageFormatter.format<string>(
          params as Record<string, string>,
        );

        if (Array.isArray(textOrPartsOfText)) {
          return textOrPartsOfText.join();
        }

        return textOrPartsOfText;
      } catch {}
    }

    return translation;
  },
);

// Translates an array of elements. For instance, the following yaml structure:
//    - text: 'TEXT'
//      link: 'LINK'
//    - list:
//      - foo: 'One'
//      - bar: 'Two'
// would result in the following array with the translated values:
//    [{ text: 'TEXT', link: 'LINK' }, { list: [{ foo: 'One' }, { bar: 'Two' }], link: false }]
export const getBusinessAwareArrayTranslator = createSelector(
  getBusinessAwareTranslator,
  i18nSelectors.translations,
  getBusinessAwareTranslationKey,
  <ParamsType extends Record<string, unknown>>(
    t: TranslatorFunction,
    translations: RecordOfRecords,
    getKey: (key: string, country?: Country, contractType?: ContractOnboardingType) => string,
  ) => (tKey: string, params?: ParamsType): Record<keyof ParamsType, string>[] => {
    // Get the key to be used according to english config
    const finalKey = getKey(tKey);

    // Get the english version
    const englishArray = get(translations, `en.${finalKey}`);

    return englishArray?.map(({ link = false, ...otherProps }, i) => {
      const otherPropsTranslated = Object.keys(otherProps).reduce(
        (acc, key) => ({
          ...acc,
          [key]: t(`${finalKey}[${i}].${key}`, (params as unknown) as Record<string, string>),
        }),
        {},
      );

      return { link, ...otherPropsTranslated };
    });
  },
);
